/**
 * HUD placement and small pure helpers (spec §A.2–A.6, §E.11). No DOM, no React:
 * everything here is unit-tested in geometry.test.ts.
 *
 * Positions are CSS px from the viewport edges. Every edge also gets
 * env(safe-area-inset-*) added by edgeStyle() (§A.4; 0 where there is none).
 */
import type { CorridorCollection, Place } from "@/contracts/data";
import type { LayerFlags, Layout, Mood, SheetSnap } from "@/contracts/store";
import { serializeUrlState, urlStateOf } from "@/features/url/urlState";
import type { LayerRow } from "@/design";
import { HUD_COPY } from "./copy";

/* ── tiers ──────────────────────────────────────────────────────────── */

/** §A.1: mobile ≤ 720 · compact desktop 721–1099 · desktop 1100–1679 · large ≥ 1680 · kiosk. */
export type HudTier = "mobile" | "compact" | "desktop" | "large" | "kiosk";

export interface TierSpec {
  gutter: number; //      G
  panel: number; //       Wp (left glass panel width)
  card: number; //        Wc (place card width)
  attrBottom: number; //  attribution pill bottom
  attrHeight: number; //  attribution pill height
  lightBottom: number; // LightIsland bottom
  /** CSS zoom on the control islands (kiosk: 40 px buttons → 56). */
  scale: number;
  /** CSS zoom on the layers card and the About sheet (kiosk type factor k). */
  cardScale: number;
}

/**
 * §A.2 desktop, §A.3 large, §A.5 kiosk. Compact desktop has no table in the
 * spec: its chrome row sits at 12, the value B10 publishes as chromeBottom
 * (layoutMath METRICS.compact), so the camera's bottom inset matches.
 */
export const TIERS: Record<Exclude<HudTier, "mobile">, TierSpec> = {
  compact: { gutter: 16, panel: 344, card: 360, attrBottom: 12, attrHeight: 24, lightBottom: 12, scale: 1, cardScale: 1 },
  desktop: { gutter: 24, panel: 376, card: 392, attrBottom: 16, attrHeight: 24, lightBottom: 16, scale: 1, cardScale: 1 },
  large: { gutter: 32, panel: 420, card: 420, attrBottom: 24, attrHeight: 24, lightBottom: 24, scale: 1, cardScale: 1 },
  kiosk: { gutter: 32, panel: 480, card: 460, attrBottom: 24, attrHeight: 28, lightBottom: 32, scale: 1.4, cardScale: 1.2 },
};

/** The ZoomIsland column at scale 1: 40 px buttons + 4 px padding each side. */
export const ZOOM_COLUMN = 48;
/** Gap between the zoom column and the layers card (.ld-layers right: 62 = 48 + 14). */
export const LAYERS_GAP = 14;
/** The aesthetic hides the LightIsland arc at 1320 px and below. */
export const ARC_MIN_WIDTH = 1321;
/** Rendered LightIsland sizes at scale 1 (measured in Chromium at ?harness=hud: 278.2, 168.2 and 160 wide; 76 and 58 high). */
export const LIGHT_W = { arc: 280, noArc: 170, compact: 160 } as const;
/**
 * Rendered attribution pill widths at 10 px (measured the same way): the full
 * line with "Powered by Flux Real" (577.8), the short line (201.8), and what
 * "Powered by Flux Real" adds to the short line (160.3).
 */
export const ATTR_W = { full: 580, short: 204, powered: 162 } as const;
/** Clear space kept between the LightIsland and the attribution pill. */
export const ATTR_CLEAR = 16;

export const MOBILE = { gutter: 12, top: 16, button: 44, gap: 8, sheetGap: 12 } as const;

export function hudTier(layout: Layout, kiosk: boolean, width: number): HudTier {
  if (kiosk || layout === "kiosk") return "kiosk";
  if (layout === "mobile") return "mobile";
  if (width >= 1680) return "large";
  if (width >= 1100) return "desktop";
  return "compact";
}

/* ── mobile sheet ───────────────────────────────────────────────────── */

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** §A.4 sheet heights above the bottom edge (safe-area inset not included). Same numbers as B10's sheetHeights(). */
export function sheetHeight(snap: SheetSnap, vh: number, selected: boolean): number {
  const peek = 130;
  const full = Math.max(peek, Math.round(vh - 72));
  if (snap === "full") return full;
  if (snap === "half") return Math.round(clamp(selected ? Math.min(460, 0.6 * vh) : clamp(0.5 * vh, 360, 480), peek, full));
  return peek;
}

/* ── geometry ───────────────────────────────────────────────────────── */

export interface Edges {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

export interface HudGeometry {
  tier: HudTier;
  light: Edges & { compact: boolean; scale: number };
  /** Desktop and kiosk: the + − · home · layers column. */
  zoom: (Edges & { scale: number }) | null;
  /** Mobile: "Back to the site" + "Layers" as solo glass buttons. */
  mapButtons: (Edges & { size: number; gap: number }) | null;
  /** Share this view · Full screen. null on mobile and kiosk. */
  tools: Edges | null;
  attr: Edges & { compact: boolean; height: number; powered: boolean; expandable: boolean };
  /** The layers card, relative to the control column's bottom-right corner (already divided by its own scale). */
  layers: { right: number; bottom: number; scale: number };
  /** Mobile sheet at full: the list carries the attribution; the map buttons step aside. */
  hideBottom: boolean;
}

export interface GeometryInput {
  tier: HudTier;
  width: number;
  height: number;
  cardOpen: boolean;
  /** Mobile only: sheet height above the bottom edge. */
  sheet?: number;
  sheetSnap?: SheetSnap;
}

export function lightWidth(width: number): number {
  return width >= ARC_MIN_WIDTH ? LIGHT_W.arc : LIGHT_W.noArc;
}

interface AttrFit {
  compact: boolean;
  powered: boolean;
  w: number;
}
const ATTR_FITS: readonly AttrFit[] = [
  { compact: false, powered: true, w: ATTR_W.full },
  { compact: true, powered: true, w: ATTR_W.short + ATTR_W.powered },
  { compact: true, powered: false, w: ATTR_W.short },
];

export interface BottomRowFit {
  lightCompact: boolean;
  attr: AttrFit;
  /** Nothing fits beside the LightIsland: it rises above the attribution row. */
  raised: boolean;
}

/**
 * The bottom row shares one line: LightIsland on the left (after the panel),
 * attribution on the right. Prefer the full LightIsland and the full line;
 * give way in this order: the full line → "Powered by" (the About sheet keeps
 * it) → the LightIsland's clock → the row itself (the LightIsland rises above
 * the pill, so the attribution always keeps its corner). An open card that
 * would reach the LightIsland also takes its clock (§A.2: the card sits left
 * of the zoom column).
 */
export function fitBottomRow(width: number, g: number, lightLeft: number, lightFull: number, lightSmall: number, cardCompact: boolean): BottomRowFit {
  const fits = (a: AttrFit, lw: number) => width - g - a.w >= lightLeft + lw + ATTR_CLEAR;
  const a = ATTR_FITS.find((f) => fits(f, cardCompact ? lightSmall : lightFull));
  if (a) return { lightCompact: cardCompact, attr: a, raised: false };
  const b = cardCompact ? undefined : ATTR_FITS.find((f) => fits(f, lightSmall));
  if (b) return { lightCompact: true, attr: b, raised: false };
  // Raised: the pill has the row to itself, from the panel's edge to the gutter.
  const c = ATTR_FITS.find((f) => width - g - f.w >= lightLeft) ?? ATTR_FITS[ATTR_FITS.length - 1];
  return { lightCompact: cardCompact, attr: c, raised: true };
}

export function hudGeometry(i: GeometryInput): HudGeometry {
  if (i.tier === "mobile") {
    const snap = i.sheetSnap ?? "peek";
    // At full the row steps aside where it stood (over half), fading in place
    // rather than riding up the screen under the rising sheet.
    const sheet = Math.max(0, snap === "full" ? sheetHeight("half", i.height, i.cardOpen) : (i.sheet ?? sheetHeight(snap, i.height, i.cardOpen)));
    const bottom = sheet + MOBILE.sheetGap;
    return {
      tier: "mobile",
      light: { right: MOBILE.gutter, top: MOBILE.top, compact: true, scale: 1 },
      zoom: null,
      mapButtons: { right: MOBILE.gutter, bottom, size: MOBILE.button, gap: MOBILE.gap },
      tools: null,
      attr: { left: MOBILE.gutter, bottom, compact: true, height: 24, powered: false, expandable: true },
      layers: { right: MOBILE.button + MOBILE.gap, bottom: 0, scale: 1 },
      hideBottom: snap === "full",
    };
  }
  const t = TIERS[i.tier];
  const g = t.gutter;
  const lightLeft = g + t.panel + 16;
  const column = ZOOM_COLUMN * t.scale;
  const lightFull = lightWidth(i.width) * t.scale;
  let fit: BottomRowFit = { lightCompact: false, attr: ATTR_FITS[0], raised: false };
  if (i.tier !== "kiosk") {
    const cardLeft = i.width - (g + column + 12) - t.card;
    const cardCompact = i.cardOpen && lightLeft + lightFull + 12 > cardLeft;
    fit = fitBottomRow(i.width, g, lightLeft, lightFull, LIGHT_W.compact * t.scale, cardCompact);
  }
  const { lightCompact, attr } = fit;
  return {
    tier: i.tier,
    light: { left: lightLeft, bottom: fit.raised ? t.attrBottom + t.attrHeight + 8 : t.lightBottom, compact: lightCompact, scale: t.scale },
    zoom: { right: g, bottom: t.attrBottom + t.attrHeight + 12, scale: t.scale },
    mapButtons: null,
    tools: i.tier === "kiosk" ? null : { right: g, top: g },
    attr: { right: g, bottom: t.attrBottom, compact: attr.compact, height: t.attrHeight, powered: attr.powered, expandable: false },
    layers: { right: (column + LAYERS_GAP) / t.cardScale, bottom: 0, scale: t.cardScale },
    hideBottom: false,
  };
}

/** Inline position: px from the edges, each plus its env(safe-area-inset-*). */
export function edgeStyle(e: Edges): Record<string, string> {
  const out: Record<string, string> = {};
  for (const side of ["top", "right", "bottom", "left"] as const) {
    const v = e[side];
    if (v != null) out[side] = `calc(${Math.round(v * 100) / 100}px + env(safe-area-inset-${side}, 0px))`;
  }
  return out;
}

/* ── share this view ────────────────────────────────────────────────── */

/**
 * "Share this view": the page with the public state only (place, cat, mood), the way the card's "Share this
 * place" builds its link. Never the internal or tuning parameters of the page it was copied from (?qa,
 * light, intro, kiosk …): a recipient gets the view, not the debug hooks.
 */
export function shareViewUrl(loc: { origin: string; pathname: string }, s: { selectedId: string | null; category: string | null; mood: Mood }): string {
  return `${loc.origin}${loc.pathname}${serializeUrlState("", urlStateOf(s))}`;
}

/* ── zoom ───────────────────────────────────────────────────────────── */

export const ZOOM_EPS = 0.01;

export function zoomLimits(z: number, min: number, max: number): { atMin: boolean; atMax: boolean } {
  return { atMin: z <= min + ZOOM_EPS, atMax: z >= max - ZOOM_EPS };
}

/**
 * Target of a ± 1 zoom press. Presses during a zoom ease stack on its target
 * (two quick presses = two levels), clamped to the camera limits. null = no move.
 */
export function nextZoom(current: number, pending: number | null, dir: 1 | -1, min: number, max: number): number | null {
  const base = pending ?? current;
  const t = clamp(base + dir, min, max);
  return Math.abs(t - base) < ZOOM_EPS ? null : t;
}

/* ── layers card rows ───────────────────────────────────────────────── */

/** LAYER_ROWS with the §F labels, the boundary note, and hillshade waiting for the DEM. */
export function layerRows(base: readonly LayerRow[], demReady: boolean): LayerRow[] {
  return base.map((r) => {
    const label = HUD_COPY.layerLabels[r.key] ?? r.label;
    if (r.key === "boundary") return { ...r, label, note: HUD_COPY.boundaryNote };
    if (r.key === "hillshade" && !demReady) return { ...r, label, disabled: true, note: HUD_COPY.hillshadeWaiting };
    return { ...r, label };
  });
}

export const LAYER_KEYS: readonly (keyof LayerFlags)[] = ["routes", "future", "contours", "hillshade", "rings", "boundary", "labels"];

/* ── about sheet data ───────────────────────────────────────────────── */

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "2026-09-23T20:22:04Z" → "23 September 2026" (the date as written, no timezone shift). "" when unreadable. */
export function formatIsoDate(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  if (!m) return "";
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return "";
  return `${d} ${MONTHS[mo - 1]} ${m[1]}`;
}

/** Drops the trailing status the data puts in names: "Outer Ring Road (under construction)" → "Outer Ring Road". */
export function cleanCorridorName(name: string): string {
  return name.replace(/\s*\((proposed|under construction|under-construction|indicative|planned)\)\s*$/i, "").trim();
}

export type CorridorStatus = "existing" | "proposed" | "under-construction" | "indicative";
export interface FutureRow {
  id: string;
  name: string;
  status: CorridorStatus;
  source: string;
  date: string;
}

const STATUS_ORDER: Record<CorridorStatus, number> = { "under-construction": 0, proposed: 1, indicative: 2, existing: 3 };

/**
 * Future corridors for the About sheet: firmest status first, then by name.
 * The source line is the plain-language one from HUD_COPY.futureSources when
 * the corridor is known, else the data's own `source` string. A corridor that is
 * a place (`placeId`) carries that place's name and status, so the About sheet,
 * list, card and map say the same thing (QA L3-7).
 */
export function futureRows(
  future: CorridorCollection | null | undefined,
  sources: Readonly<Record<string, string>> = {},
  places: ReadonlyArray<Pick<Place, "id" | "name" | "status">> = [],
): FutureRow[] {
  if (!future) return [];
  const seen = new Set<string>();
  const rows: FutureRow[] = [];
  for (const f of future.features) {
    const p = f.properties;
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    const source = (sources[p.id] ?? p.source).trim().replace(/\.$/, "");
    const place = p.placeId ? places.find((x) => x.id === p.placeId) : undefined;
    const status: CorridorStatus = place && place.status !== "existing" && place.status !== "upcoming" ? place.status : p.status;
    rows.push({ id: p.id, name: place?.name ?? cleanCorridorName(p.name), status, source, date: formatIsoDate(p.sourceDate) });
  }
  return rows.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.name.localeCompare(b.name));
}

export interface SourceCounts {
  clientPin: number;
  osm: number;
  geocode: number;
  manual: number;
  total: number;
  advised: number; // places with a drive time advised by Indulkar (overrides)
}

/** Where the place pins came from (the site rows are never counted). */
export function sourceCounts(places: readonly Place[] | null | undefined): SourceCounts {
  const c: SourceCounts = { clientPin: 0, osm: 0, geocode: 0, manual: 0, total: 0, advised: 0 };
  for (const p of places ?? []) {
    if (p.isSite) continue;
    c.total++;
    if (p.source === "client-pin") c.clientPin++;
    else if (p.source === "osm") c.osm++;
    else if (p.source === "geocode") c.geocode++;
    else if (p.source === "manual") c.manual++;
    if (p.route && (p.route.kmOverride != null || p.route.minutesOverride != null)) c.advised++;
  }
  return c;
}
